/**
 * Catalog for the Regional Sales (rep) and Regional Sales Oversight
 * (admin) surfaces — the "what each tab is for and how to use it"
 * source of truth behind the User Guide.
 *
 * Kept in the same shape as `depletionsSections.ts` so the guide page
 * renders all three product areas with one component.
 *
 * The rep workspace lives at `/regional-sales`; the admin oversight
 * surface at `/admin/regional-sales`. Internally both still speak to
 * `/api/field/*` and gate on the `field_rep` role — the customer-facing
 * name changed, the identifiers did not.
 */

import type { SectionGuide } from './depletionsSections';

export interface GuideTopic {
  /** Stable id — DOM anchor + screenshot filename. */
  id: string;
  title: string;
  /** One-line description shown under the title. */
  description: string;
  guide: SectionGuide;
}

// ---------------------------------------------------------------------
// Regional Sales — the representative's own workspace
// ---------------------------------------------------------------------

export const REGIONAL_SALES_TABS: GuideTopic[] = [
  {
    id: 'todays-plan',
    title: "Today's Plan",
    description: 'A short, ranked list of the accounts worth your time today',
    guide: {
      what: 'Your starting screen: a shortlist drawn from your whole territory and ordered by likely impact.',
      how: [
        'Read the chip on each row — it names the one reason the account surfaced, with a sentence explaining it.',
        'Log a visit after you make contact, or skip a row you will not get to.',
        'Pin an account to keep it at the top tomorrow.',
      ],
      tips: [
        'Add your home address in Account Settings and the list starts preferring accounts near you. Recently visited accounts are held back until their cooldown passes.',
      ],
      admin:
        'An administrator can open this page, but it will be empty — they have no territory and no picks of their own. That is why the home page hides the card for them. To watch the team rather than work accounts, use Regional Sales Oversight; to work accounts too, have the Regional Sales Representative role added.',
    },
  },
  {
    id: 'my-territories',
    title: 'My Territories',
    description: 'Every account in your assigned states, in the Follow-Up Tracker layout',
    guide: {
      what: 'Every account in your states, in the same recency buckets the Depletions dashboard uses — search your whole territory and log a visit from any row.',
      how: [
        'Click a bucket, then filter by momentum or state, or search for an account.',
        'Press Log Visit on the account’s row to record a visit or call on the spot.',
        'Open any account for its history.',
      ],
      tips: [
        'Pin, Skip and Flag stay on Today’s Plan. If the tab is empty, you have no territory states assigned yet — ask an admin.',
      ],
    },
  },
  {
    id: 'manually-added',
    title: 'Manually Added Accounts',
    description: 'Accounts a person typed in, which the depletions feed has not confirmed yet',
    guide: {
      what: 'Accounts someone entered by hand — typically somewhere visited before it ever appeared in a depletions file.',
      how: [
        'Use "Add an Account" when you are somewhere that is not in your list.',
        'Type the name first: near matches appear as you type, so you can pick the existing account instead of creating a duplicate.',
      ],
      tips: [
        'With no depletion history they cannot appear in the recency buckets, so they wait here until a depletions file confirms them. The tab is territory-scoped, so you will see colleagues’ additions too.',
      ],
    },
  },
  {
    id: 'no-shipments',
    title: 'No Shipments Yet',
    description: 'Accounts in your states we have never sold to',
    guide: {
      what: 'Accounts on file with no shipment on record. They cannot appear in Today’s Plan or My Territories, which both rank by how long since the last one — so without this tab they were reachable from nowhere.',
      how: [
        'Search by name, city or address to find one, or switch states if you are travelling.',
        'Open an account for its detail, or log a visit straight from the row.',
      ],
      tips: [
        'Check here before using "Add an Account" — the account you are standing in front of may already be on file. Rows marked "Manually added" were typed in by a person and are waiting on their first depletions file.',
      ],
    },
  },
  {
    id: 'my-logs',
    title: 'My Logs',
    description: 'Everything you have recorded, across every account, newest first',
    guide: {
      what: 'Everything you have logged, flagged, and skipped, in one feed.',
      how: ['Use the filter chips to narrow to just visits, just flags, or just skips.'],
      tips: ['Grouped by the day the thing happened, not the day you typed it.'],
    },
  },
  {
    id: 'team-chat',
    title: 'Team Chat',
    description: 'Direct and group messages with anyone on the platform',
    guide: {
      what: 'Direct and group messages, available to every user — not just reps.',
      how: ['Open a conversation, or start a new one with a teammate or group.'],
    },
  },
];

/** Things a rep does on an account, documented once and reused. */
export const REGIONAL_SALES_ACTIONS: GuideTopic[] = [
  {
    id: 'log-visit',
    title: 'Logging a visit',
    description: 'Record what happened after any contact with an account',
    guide: {
      what: 'The record of one contact: a date, a channel, an outcome, a note, and any products that came up.',
      how: [
        'Check "Previous touchpoints" at the top so you know what was said last time.',
        'Pick the channel and outcome, add the note, and save.',
      ],
      tips: [
        'The outcome sets how long the account stays off Today’s Plan — anywhere from a few days to a couple of months. The dialog tells you the exact date before you save.',
      ],
      admin:
        'Every note lands in the Oversight activity feed, so administrators see it alongside the rest of the team’s. A note logged by an administrator while acting as a rep is saved to that rep and audit-logged to the administrator.',
    },
  },
  {
    id: 'flag-account',
    title: 'Flagging an account',
    description: 'Escalate a problem with the account record itself to an admin',
    guide: {
      what: 'A request for an admin to fix the record — a closed store, a new buyer, a wrong address.',
      how: ['Open the account, choose Flag, and write the reason.'],
      tips: [
        'Not a sixth outcome. Outcomes describe how a conversation went; a flag asks someone to correct the data.',
      ],
      admin:
        'Flags are written to the audit trail. Administrators review them under Admin → Audit Log; there is no separate flag queue.',
    },
  },
  {
    id: 'account-detail',
    title: 'The account page',
    description: 'Contacts, history, and every note logged against one account',
    guide: {
      what: "One account's contacts, performance, and full visit timeline — including notes left by other reps.",
      how: [
        'Add or edit contacts as you learn them.',
        'Log a visit or pin the account from the header.',
      ],
      tips: ['Numbers and addresses are live links — tap to dial or open maps.'],
      admin:
        'The timeline shows every rep’s notes on the account, not just your own — the same entries administrators see in the Oversight feed.',
    },
  },
];

// ---------------------------------------------------------------------
// Regional Sales Oversight — the admin surface
// ---------------------------------------------------------------------

export const OVERSIGHT_TOPICS: GuideTopic[] = [
  {
    id: 'rep-roster',
    title: 'The representative roster',
    description: 'Every rep, their territory, and their recent activity counters',
    guide: {
      what: 'Everyone holding the Regional Sales Representative role, with their states and how much they have logged recently.',
      how: [
        'Click a rep to filter the activity feed to their work; click again to clear it.',
        'Use the pencil to open their profile and territory.',
      ],
    },
  },
  {
    id: 'activity-feed',
    title: 'The activity feed',
    description: 'Every visit note from every rep, grouped by day, newest first',
    guide: {
      what: 'One stream of what the whole field team has been doing, with each entry’s rep, account, channel, and outcome.',
      how: ['Click a rep in the roster to narrow the feed to them.'],
    },
  },
  {
    id: 'edit-rep',
    title: "Setting a rep's profile and territory",
    description: 'Assign the states a rep works and maintain their details',
    guide: {
      what: 'Where you decide which states a representative covers.',
      how: ['Add or remove territory states, then save.'],
      tips: [
        'Territory decides everything they see. Their home address, which drives the "near you" ranking, is theirs to set in Account Settings.',
      ],
    },
  },
  {
    id: 'view-as-rep',
    title: 'Viewing as a representative',
    description: 'See and act on the app exactly as one rep experiences it',
    guide: {
      what: 'Opens the rep workspace scoped to one person — useful for troubleshooting, or for covering someone who is out.',
      how: ['Choose "Act As Rep" on a roster card.', 'Return to the roster to exit.'],
      tips: [
        'A banner stays on screen the whole time. Anything you log is saved to that rep and audit-logged to you.',
      ],
    },
  },
];
